import {announce} from '@github-ui/aria-live'
import {WithShimmerEffect} from './WithShimmerEffect'
import {Stack} from '@primer/react'
import {useEffect, useState} from 'react'
import {WorkingAsciiLoader} from './WorkingAsciiLoader'
import styles from './LoadingMessage.module.css'

export interface LoadingMessageProps {
  /**
   * The message to display alongside the loading indicator. Pass a single
   * string for a static message, or an array of strings to cycle through
   * them on a timer (stopping on the last entry).
   */
  message: string | readonly string[]
  /**
   * Interval in milliseconds between message changes when `message` is an
   * array of more than one string. Ignored otherwise. Defaults to 7000.
   */
  intervalMs?: number
  /**
   * When `message` is an array, controls whether subsequent message changes
   * are announced to assistive technology via a polite live region. Set to
   * `false` for purely decorative or playful loading copy where updates do
   * not convey meaningful progress. Defaults to `true`.
   */
  announceUpdates?: boolean
  /**
   * Displays determinate progress as a percentage and a decorative character
   * bar. Values are rounded and clamped between 0 and 100.
   */
  progress?: number
}

const DEFAULT_INTERVAL_MS = 7000
const PROGRESS_BAR_LENGTH = 10

function ProgressBar({progress}: {progress: number}) {
  const filledLength = Math.floor((progress / 100) * PROGRESS_BAR_LENGTH)
  const bar = `${'█'.repeat(filledLength)}${'░'.repeat(PROGRESS_BAR_LENGTH - filledLength)}`

  return (
    <span aria-hidden="true" className={styles.progressBar}>
      {bar}
    </span>
  )
}

export function LoadingMessage({
  message,
  intervalMs = DEFAULT_INTERVAL_MS,
  announceUpdates = true,
  progress,
}: LoadingMessageProps) {
  const messages = Array.isArray(message) ? message : [message]
  const [index, setIndex] = useState(0)
  const [prevLength, setPrevLength] = useState(messages.length)
  if (messages.length !== prevLength) {
    setPrevLength(messages.length)
    setIndex(0)
  }

  useEffect(() => {
    if (messages.length <= 1) return
    const interval = setInterval(() => {
      setIndex(prev => (prev < messages.length - 1 ? prev + 1 : prev))
    }, intervalMs)
    return () => clearInterval(interval)
  }, [messages.length, intervalMs])

  const currentMessage = messages[index] ?? messages[0] ?? ''
  const normalizedProgress =
    progress === undefined || !Number.isFinite(progress) ? undefined : Math.min(100, Math.max(0, Math.round(progress)))

  // Announce subsequent message changes to assistive technology via a polite
  // live region. The initial message is skipped because it is already present
  // in the reading flow when the component mounts.
  useEffect(() => {
    if (!announceUpdates) return
    if (index > 0 && currentMessage) {
      announce(currentMessage)
    }
  }, [announceUpdates, index, currentMessage])

  return (
    <Stack
      direction="horizontal"
      padding="none"
      align="center"
      justify="start"
      gap="condensed"
      className={styles.loadingMessage}
    >
      <WorkingAsciiLoader />
      {normalizedProgress === undefined ? (
        <WithShimmerEffect>{currentMessage}</WithShimmerEffect>
      ) : (
        <WithShimmerEffect innerClassName={styles.progressMessage}>
          <span
            role="progressbar"
            aria-label={currentMessage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={normalizedProgress}
            aria-valuetext={`${normalizedProgress}%`}
            className={styles.progressContent}
          >
            <span>
              {currentMessage} <span className={styles.progressPercentage}>({normalizedProgress}%)</span>
            </span>
            <ProgressBar progress={normalizedProgress} />
          </span>
        </WithShimmerEffect>
      )}
    </Stack>
  )
}
